import type { FiscalDetails, FiscalInvoicePrintData, Order } from '@/types'
import type { CustomerReceiptSettings } from '@/lib/printCustomerReceipt'
import { FBR_DIGITAL_INVOICING_LOGO_URL } from '@/lib/fbrBranding'

function esc(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function money(n: number): string {
  if (!Number.isFinite(n)) return '—'
  return n.toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

/**
 * Full-width FBR digital invoice block for thermal/HTML receipts when
 * `fiscal_details.invoice_print` is populated after a successful DI post.
 */
export function buildFbrDigitalInvoiceSection(
  order: Order,
  cfg: CustomerReceiptSettings,
  fd: FiscalDetails,
): string {
  const inv = fd.invoice_print as FiscalInvoicePrintData | undefined
  if (!inv || !inv.lines?.length) return ''

  const fbrMark = `<div class="fbr-di-fbr-mark"><img src="${esc(FBR_DIGITAL_INVOICING_LOGO_URL)}" alt="FBR POS Invoicing System" style="height:44px;width:auto;object-fit:contain;display:inline-block"/></div>`

  const logo =
    cfg.logoUrl && /^(https?:\/\/|data:image\/)/i.test(cfg.logoUrl)
      ? `<div class="fbr-di-logo"><img src="${esc(cfg.logoUrl)}" alt="" style="max-width:${Math.round(cfg.logoWidthPercent ?? 75)}%;max-height:48px;object-fit:contain" /></div>`
      : ''

  let qr = ''
  const rawB64 = String(fd.qr_code_png_base64 ?? '').replace(/\s/g, '')
  if (/^[A-Za-z0-9+/]+=*$/.test(rawB64) && rawB64.length > 80) {
    qr = `<div class="fbr-di-qr"><img src="data:image/png;base64,${rawB64}" alt="FBR verification"/></div>`
  }

  const irn = String(fd.irn ?? '').trim()

  const lineRows = inv.lines
    .map((ln) => {
      const sub = [
        esc(ln.product_description || '—'),
        esc(ln.hs_code || '—'),
        esc(ln.uo_m || '—'),
        String(ln.quantity ?? ''),
        money(ln.value_sales_excluding_st ?? 0),
        esc(ln.rate || '—'),
        money(ln.sales_tax_applicable ?? 0),
        money(ln.total_values ?? 0),
        esc(ln.sale_type || ''),
        esc(ln.fbr_line_invoice_no || '—'),
      ]
      return `<tr><td class="fbr-d">${sub[0]}</td><td class="fbr-n">${sub[1]}</td><td class="fbr-s">${sub[2]}</td><td class="fbr-n">${sub[3]}</td><td class="fbr-n">${sub[4]}</td><td class="fbr-s">${sub[5]}</td><td class="fbr-n">${sub[6]}</td><td class="fbr-n">${sub[7]}</td><td class="fbr-s">${sub[8]}</td><td class="fbr-s fbr-mono">${sub[9]}</td></tr>`
    })
    .join('')

  const ob = (l: string, v: string) =>
    `<div class="fbr-di-ob"><span>${esc(l)}</span><span>${esc(v)}</span></div>`

  return `
  <section class="fbr-di-invoice" aria-label="FBR digital tax invoice">
    <div class="fbr-di-bar"></div>
    <div class="fbr-di-head">
      ${fbrMark}
      ${logo}
      <div class="fbr-di-title">Digital tax invoice (FBR)</div>
      <div class="fbr-di-sub">Order #${esc(order.order_number)} · Ref ${esc(inv.invoice_ref_no || fd.fiscal_usin || order.id)}</div>
    </div>

    <div class="fbr-di-seller">
      <div class="fbr-di-block-title">Seller</div>
      ${ob('Business', inv.seller.name ?? '—')}
      ${ob('STRN / NTN CNIC', inv.seller.ntn_cnic ?? '—')}
      ${ob('Province', inv.seller.province ?? '—')}
      <div class="fbr-di-multiline">${esc(inv.seller.address ?? '')}</div>
    </div>

    <div class="fbr-di-buyer">
      <div class="fbr-di-block-title">Buyer</div>
      ${ob('Type', inv.buyer.registration_type || '—')}
      ${ob('Name', inv.buyer.name || '—')}
      ${ob('NTN / CNIC', inv.buyer.ntn_cnic || '—')}
      ${ob('Province', inv.buyer.province || '—')}
      <div class="fbr-di-multiline">${esc(inv.buyer.address || '')}</div>
    </div>

    <div class="fbr-di-meta">
      ${ob('Invoice type', inv.invoice_type)}
      ${ob('Invoice date', inv.invoice_date)}
      ${ob('Scenario', inv.scenario_id || '—')}
      ${inv.fbr_posted_at ? ob('FBR dated', inv.fbr_posted_at) : ''}
    </div>

    <div class="fbr-di-lines-scroll">
      <table class="fbr-di-lines">
        <thead>
          <tr>
            <th>Description</th><th>HS</th><th>UoM</th><th>Qty</th><th>Ex-ST</th><th>Rate</th><th>ST</th><th>Total</th><th>Sale type</th><th>FBR line #</th>
          </tr>
        </thead>
        <tbody>${lineRows}</tbody>
      </table>
    </div>

    <div class="fbr-di-totals">
      ${ob('Total excl. ST', money(inv.totals_value_excl_st ?? 0))}
      ${ob('Sales tax', money(inv.totals_sales_tax ?? 0))}
      ${ob('Grand total', money(inv.totals_grand ?? 0))}
    </div>

    <div class="fbr-di-footer">
      ${qr}
      <div class="fbr-di-irn">
        <div class="fbr-di-irn-lbl">FBR invoice / IRN</div>
        <div class="fbr-di-irn-val">${esc(irn)}</div>
        <div class="fbr-di-hint">Verify via FBR or authorized tax verification channels.</div>
      </div>
    </div>
  </section>`
}
